import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Upload as UploadIcon, X } from 'lucide-react';
import { FINISHING_TYPES } from '@/utils/constants';

// Two-section layout matches the CAD sheet so the same task round-trips
// without the form and the CAD form drifting apart on these fields.
export function TaskFinishingTab({
  formData,
  onChange,
  canEdit,
  onUploadEngravingImage,
  onUploadStampingImages,
}) {
  const finishingValue = formData.finishing || '';
  const finishingIsKnown = FINISHING_TYPES.includes(finishingValue);

  const handleEngravingFile = (e) => {
    const file = e.target.files?.[0];
    if (file && onUploadEngravingImage) onUploadEngravingImage(file);
    e.target.value = '';
  };

  const handleStampingFiles = (e) => {
    const files = e.target.files;
    if (files && onUploadStampingImages) Array.from(files).forEach((f) => onUploadStampingImages(f));
    e.target.value = '';
  };

  const removeStampingImage = (idx) => {
    const next = (formData.stamping_images || []).filter((_, i) => i !== idx);
    onChange('stamping_images', next);
  };

  return (
    <div className="space-y-6">
      {/* Section 1 — Engraving & Stamping */}
      <div className="border rounded-lg p-4 bg-slate-50/40 space-y-4">
        <h3 className="text-lg font-semibold">Engraving &amp; Stamping</h3>

        <div>
          <Label htmlFor="engraving">Engraving Instructions</Label>
          <Textarea
            id="engraving"
            value={formData.engraving || ''}
            onChange={(e) => onChange('engraving', e.target.value)}
            disabled={!canEdit}
            placeholder="Engraving text or instructions..."
            rows={2}
            className="mt-2"
          />
        </div>

        <div>
          <Label>Engraving Image Reference</Label>
          {formData.engraving_image ? (
            <div className="relative w-32 h-32 border rounded-lg overflow-hidden mt-2">
              <img src={formData.engraving_image} alt="Engraving ref" className="w-full h-full object-contain bg-white" />
              {canEdit && (
                <button
                  type="button"
                  onClick={() => onChange('engraving_image', '')}
                  className="absolute top-1 right-1 p-1 bg-rose-500 text-white rounded-full"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          ) : canEdit ? (
            <label className="flex items-center gap-2 mt-2 px-3 py-2 border border-dashed rounded-lg cursor-pointer hover:bg-slate-100 w-fit">
              <UploadIcon size={16} className="text-slate-500" />
              <span className="text-sm text-slate-600">Upload engraving reference</span>
              <input type="file" accept="image/*" className="hidden" onChange={handleEngravingFile} />
            </label>
          ) : (
            <p className="text-xs text-slate-400 mt-2">No engraving reference uploaded</p>
          )}
        </div>

        <div>
          <Label htmlFor="stamping_instructions">Stamping Instructions</Label>
          <Textarea
            id="stamping_instructions"
            value={formData.stamping_instructions || ''}
            onChange={(e) => onChange('stamping_instructions', e.target.value)}
            disabled={!canEdit}
            placeholder="Hallmark, brand stamp, etc."
            rows={2}
            className="mt-2"
          />
        </div>

        <div>
          <Label>Stamping Image Reference(s)</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {(formData.stamping_images || []).map((url, i) => (
              <div key={`${url}-${i}`} className="relative w-24 h-24 border rounded-lg overflow-hidden">
                <img src={url} alt={`Stamping ref ${i + 1}`} className="w-full h-full object-contain bg-white" />
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => removeStampingImage(i)}
                    className="absolute top-1 right-1 p-1 bg-rose-500 text-white rounded-full"
                  >
                    <X size={10} />
                  </button>
                )}
              </div>
            ))}
            {canEdit && (
              <label className="flex flex-col items-center justify-center w-24 h-24 border border-dashed rounded-lg cursor-pointer hover:bg-slate-100">
                <UploadIcon size={14} className="text-slate-500" />
                <span className="text-[10px] text-slate-500 mt-1">Add stamping</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleStampingFiles} />
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Section 2 — Finishing */}
      <div className="border rounded-lg p-4 bg-slate-50/40">
        <h3 className="text-lg font-semibold mb-3">Finishing</h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <Label htmlFor="finishing">Finishing Type</Label>
            <select
              id="finishing"
              value={finishingIsKnown ? finishingValue : (finishingValue ? '__custom' : '')}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '__custom') onChange('finishing', '');
                else onChange('finishing', v);
              }}
              disabled={!canEdit}
              className="mt-2 w-full h-10 px-3 border rounded-md bg-white"
            >
              <option value="">Select finishing...</option>
              {FINISHING_TYPES.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
              <option value="__custom">Other...</option>
            </select>
            {!finishingIsKnown && finishingValue !== '' && (
              <Input
                value={finishingValue}
                onChange={(e) => onChange('finishing', e.target.value)}
                placeholder="Specify finishing..."
                disabled={!canEdit}
                className="mt-2"
              />
            )}
          </div>
          <div>
            <Label htmlFor="rhodium_plating">Rhodium Plating</Label>
            <Input
              id="rhodium_plating"
              value={formData.rhodium_plating || ''}
              onChange={(e) => onChange('rhodium_plating', e.target.value)}
              placeholder="e.g. Black rhodium accents"
              disabled={!canEdit}
              className="mt-2"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
